import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Navbar from "../components/Navbar.jsx";
import Footer from "../components/Footer.jsx";
import ImageWithFallback from "../components/ImageWithFallback";
import ProjectQuickLook from "../features/catalog/ProjectQuickLook";
import Select from "../components/Select";
import { useAuth } from "../features/auth";
import { useProjects } from "../features/catalog";

// Assets
import searchIcon from "../assets/icons/search.png";
import arrowPng from "../assets/icons/arrow.png";

// The card below reuses Showcase/LiveProjects' .fp* card so the hover (image
// darken, pill fills amber, details slide) stays defined in exactly one place.
import "../features/catalog/catalog.css";
import "./DiscoverPage.css";

export default function DiscoverPage() {
  const { region } = useParams();
  const { user, signOut } = useAuth();
  const { projects, loading, error, reload } = useProjects("Discover");

  const [selected, setSelected] = useState(null);

  const [type, setType] = useState("All");
  const [city, setCity] = useState("All");
  const [band, setBand] = useState("All");
  const [query, setQuery] = useState("");

  // open straight into a project when the URL says so: ?project=<Project ID>
  useEffect(() => {
    const wanted = new URLSearchParams(window.location.search).get("project");
    if (wanted && projects.length) {
      setSelected(projects.find((p) => p.id === wanted) || null);
    }
  }, [projects]);

  const types = [
    "All",
    ...new Set(projects.map((p) => p.constructionType).filter(Boolean)),
  ];
  const cities = [
    "All",
    ...new Set(projects.map((p) => p.city).filter(Boolean)),
  ];

  const BANDS = {
    All: () => true,
    "< 5k sqft": (a) => a != null && a < 5000,
    "5k – 25k sqft": (a) => a != null && a >= 5000 && a <= 25000,
    "25k+ sqft": (a) => a != null && a > 25000,
  };

  // Search every human-readable field. URL/ID columns are excluded on purpose — they add noise.
  const haystack = (p) =>
    [p.name, p.sbu, p.constructionType, p.industry, p.country, p.city, p.description]
      .join(" ")
      .toLowerCase();

  const q = query.trim().toLowerCase();
  const visible = projects.filter(
    (p) =>
      (type === "All" || p.constructionType === type) &&
      (city === "All" || p.city === city) &&
      BANDS[band](p.areaSqft) &&
      (!q || haystack(p).includes(q))
  );

  // "All" is the sentinel the filters compare against; the dropdown needs a
  // friendlier label for it, and `muted` because it names the filter's category
  // rather than a value — an unset filter should recede, not shout.
  const options = (values, allLabel) =>
    values.map((v) => ({
      value: v,
      label: v === "All" ? allLabel : v,
      muted: v === "All",
    }));

  const filtered = type !== "All" || city !== "All" || band !== "All" || q !== "";

  const clearAll = () => {
    setType("All");
    setCity("All");
    setBand("All");
    setQuery("");
  };

  return (
    <div className="dv-page-container">
      {/* ponytail: Navbar derives the region from the path itself; the
          selectedServer prop pair was write-only state here. */}
      <Navbar user={user} signOut={signOut} />

      <main className="dv-main-content">
        <header className="dv-header-top">
          <div className="dv-header-left">
            <div className="dv-title-row">
              <h1 className="dv-heading">Discover</h1>
            </div>

            <p className="dv-description">
              Explore our premium architectural visualizations and immersive 3D
              walkthroughs
            </p>
          </div>
        </header>

        <section className="dv-filter-bar">
          <div className="dv-filter-controls">
            <Select
              value={type}
              onChange={setType}
              options={options(types, "All Construction Types")}
              label="Filter by construction type"
            />

            <Select
              value={city}
              onChange={setCity}
              options={options(cities, "All Cities")}
              label="Filter by city"
            />

            <Select
              value={band}
              onChange={setBand}
              options={options(Object.keys(BANDS), "All Areas")}
              label="Filter by area"
            />

            {filtered && (
              <button type="button" className="dv-clear-all" onClick={clearAll}>
                Clear all
              </button>
            )}
          </div>

          <div className="dv-filter-controls">
            <div className="dv-search-box">
              <img src={searchIcon} alt="" className="dv-search-icon-img" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search Projects..."
              />
            </div>
          </div>
        </section>

        {loading && (
          <div className="dv-loader-container">
            <div className="dv-loader-ring"></div>
            <p className="dv-loader-text">Curating projects for you…</p>
          </div>
        )}

        {error && (
          <div className="dv-error">
            <p>Couldn't load projects.</p>
            <button className="dv-retry-btn" onClick={reload} type="button">
              Retry
            </button>
          </div>
        )}

        {!loading && !error && (
          <div className="dv-projects-grid">
            {visible.map((p) => (
              <button
                key={p.id}
                className="dv-project-card fpProjectCard"
                onClick={() => setSelected(p)}
                type="button"
              >
                <div className="fpCardMedia">
                  <ImageWithFallback
                    className="fpCardImg"
                    src={p.thumbnail}
                    alt={p.name}
                  />

                  {/* A span, not a button: the whole card is already the button
                      that opens the project, and buttons can't nest. */}
                  <span className="fpViewPill">
                    View Project
                    <img
                      className="fpViewPillArrow"
                      src={arrowPng}
                      alt=""
                      aria-hidden="true"
                    />
                  </span>
                </div>

                <div className="fpCardDetails">
                  <h3 className="fpProjectName">{p.name}</h3>
                  <p className="fpProjectMeta">
                    {[
                      p.constructionType,
                      p.areaSqft && `${p.areaSqft.toLocaleString()} sqft`,
                      p.city,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}

        {!loading && !error && visible.length === 0 && (
          <div className="dv-no-results">
            <p className="dv-no-results-title">No projects match your selection</p>
            <p className="dv-no-results-hint">Try a different city, area or construction type.</p>
          </div>
        )}
      </main>

      <Footer />

      {selected && (
        <ProjectQuickLook
          key={selected.id}
          project={selected}
          region={region}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}
